import * as batshit from "@yornaath/batshit";
import {
  AssessmentControlId,
  AssessmentId,
  EngagementId,
  SourceExtractId,
  TprmReportTemplateId,
} from "platformed-client/types";
import api from "../../../api";
import {param, typedQuery} from "../../typing";
import {resolver, scheduler} from "../../batch";

const assessedControlsById = batshit.create({
  fetcher: (ids: AssessmentControlId[]) => api.tprm.assessedControls.load(ids),
  resolver: resolver("assessment_control_id"),
  scheduler,
});

const sourceExtracts = batshit.create({
  fetcher: api.tprm.assessedControls.loadSourceExtracts,
  resolver: resolver("source_extract_id"),
  scheduler,
});

export const assessment = typedQuery(
  ["tprm", "assessment", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessments.get(assessmentId);
  },
);

export const assessments = typedQuery(["tprm", "assessments"], async () => {
  return await api.tprm.assessments.search({});
});

export const assessmentRisks = typedQuery(
  ["tprm", "assessmentRisks", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessments.listRisks(assessmentId);
  },
);

export const assessmentFindings = typedQuery(
  ["tprm", "assessmentFindings", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessments.listFindings(assessmentId);
  },
);

export const engagementAssessments = typedQuery(
  ["tprm", "engagementAssessments", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.assessments.search({engagement_id: engagementId});
  },
);

/** Load an assessed control by (assessmentId, assessmentControlId) — mirrors risk query pattern */
export const assessedControl = typedQuery(
  ["tprm", "assessedControl", param<AssessmentId>("assessmentId"), param<AssessmentControlId>("assessmentControlId")],
  async (_assessmentId, assessmentControlId) => {
    return await assessedControlsById.fetch(assessmentControlId);
  },
);

export const sourceExtract = typedQuery(
  ["tprm", "sourceExtract", param<SourceExtractId>("sourceExtractId")],
  async sourceExtractId => {
    return await sourceExtracts.fetch(sourceExtractId);
  },
);

export const assessmentControlIds = typedQuery(
  ["tprm", "assessmentControlIds", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessments.listAssessmentControlIds(assessmentId);
  },
);

export const assessmentControlInstances = typedQuery(
  ["tprm", "assessmentControlInstances", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessedControls.listInstances(assessmentId);
  },
);

export const assessmentNodes = typedQuery(
  ["tprm", "assessmentNodes", param<AssessmentId>("assessmentId")],
  async assessmentId => {
    return await api.tprm.assessments.listNodes(assessmentId);
  },
);

export const reportSnapshots = typedQuery(
  ["tprm", "reportSnapshots", param<AssessmentId>("assessmentId"), param<TprmReportTemplateId>("reportTemplateId")],
  async (assessmentId, reportTemplateId) => {
    return await api.tprm.assessments.listReportSnapshots(assessmentId, reportTemplateId);
  },
);
